import {
    DEGREE,
    ECE_DEGREE,
    MINIMUM_WAGE_BY_STATE,
    YEARS_EXPERIENCE,
} from 'parent-portal/jobs/postJob/payScales/constants';
import { checkForDuplicates } from 'parent-portal/jobs/postJob/payScales/problems';
import {
    GenericChoice,
    GenericQualificationChoices,
    IManagedScale,
    IPayScale,
    Qualification,
} from 'parent-portal/jobs/postJob/payScales/types';
import type { IPayScaleQualification } from 'parent-portal/jobs/postJob/payScales/types';
import type { QualificationCategory } from './formTypes';

export interface TierOption {
    key: string;
    label: string;
    chosen: number;
    choices: GenericChoice[];
    qualification: Qualification | null;
    category: QualificationCategory;
}

export interface TierOptionGroup {
    heading: string;
    options: TierOption[];
}

const EDUCATION_NAMES: string[] = [DEGREE, ECE_DEGREE];

export function mergeTierQualifications(
    baseQualifications: IPayScaleQualification[],
    tierQualifications: IPayScaleQualification[],
): IPayScaleQualification[] {
    return [
        ...tierQualifications,
        ...baseQualifications.filter(
            (row) => !tierQualifications.some((extra) => extra.qualification_id === row.qualification_id),
        ),
    ];
}

export function minimumWageForState(state: string): number | undefined {
    return MINIMUM_WAGE_BY_STATE[state as keyof typeof MINIMUM_WAGE_BY_STATE];
}

export interface PayScaleProblems {
    rate: 'toolow' | 'bad' | null;
    duplicate: boolean;
}

export function findRateProblem(rate: number | null, state: string): PayScaleProblems['rate'] {
    if (rate === null) return null;
    if (Number.isNaN(rate)) return 'bad';
    const minimumWage = minimumWageForState(state);
    if (minimumWage !== undefined && rate < minimumWage) return 'toolow';
    return null;
}

export function baseRateHasRequirements(payScales: IPayScale[]): boolean {
    return (payScales[0]?.qualifications.length ?? 0) > 0;
}

export function findPayScaleProblems(payScales: IManagedScale[], state: string): Record<string, PayScaleProblems> {
    const [base, ...tiers] = payScales;
    const merged = [
        base,
        ...tiers.map((tier) => ({
            ...tier,
            qualifications: mergeTierQualifications(base.qualifications, tier.qualifications),
        })),
    ];
    const duplicates = checkForDuplicates(merged);

    return Object.fromEntries(
        merged.map((scale) => [
            scale.id,
            { rate: findRateProblem(scale.rate, state), duplicate: !!duplicates[scale.id] },
        ]),
    );
}

export function qualificationLabel(row: IPayScaleQualification, choices: GenericQualificationChoices): string {
    const experienceQualificationId = choices.experience.find((choice) => choice.pay_scale_qualification)
        ?.pay_scale_qualification?.qualification_id;
    if (row.name === YEARS_EXPERIENCE || row.qualification_id === experienceQualificationId) {
        const match = choices.experience.find((choice) => String(choice.key) === row.value);
        return match?.value ?? `${row.value}+ years experience`;
    }

    const educationMatch = choices.education.find(
        (choice) =>
            choice.pay_scale_qualification?.qualification_id === row.qualification_id && choice.key === row.option_id,
    );
    if (educationMatch) return educationMatch.value;

    const booleanMatch = choices.boolean.find((choice) => choice.key === row.qualification_id);
    if (booleanMatch) return booleanMatch.value;

    const singleEntry = choices.single.find((entry) => entry.qualification.id === row.qualification_id);
    if (singleEntry) {
        const option = singleEntry.choices.find((choice) => choice.key === row.option_id);
        return option ? `${singleEntry.qualification.name}: ${option.value}` : singleEntry.qualification.name;
    }

    const multiEntry = choices.multi.find((entry) => entry.qualification.id === row.qualification_id);
    if (multiEntry) {
        const option = multiEntry.choices.find((choice) => choice.key === row.option_id);
        return option ? `${multiEntry.qualification.name}: ${option.value}` : multiEntry.qualification.name;
    }

    if (row.option) return `${row.name}: ${row.option}`;
    return row.name;
}

export function buildTierOptionGroups(
    base: IManagedScale,
    tier: IManagedScale,
    choices: GenericQualificationChoices,
): TierOptionGroup[] {
    const baseEducationRow = base.qualifications.find((row) => EDUCATION_NAMES.includes(row.name));
    const baseEducationOrder =
        choices.education.find((choice) => choice.key === baseEducationRow?.option_id)?.order ??
        Number.NEGATIVE_INFINITY;
    const tierEducationKey = tier.qualifications.find((row) => EDUCATION_NAMES.includes(row.name))?.option_id;

    const education = choices.education
        .filter((choice) => choice.key !== -1)
        .filter((choice) => (choice.order ?? 0) > baseEducationOrder)
        .filter((choice) => choice.key !== tierEducationKey)
        .map((choice) => ({
            key: `education-${choice.key}`,
            label: choice.value,
            chosen: choice.key,
            choices: choices.education,
            qualification: null,
            category: 'education' as QualificationCategory,
        }));

    const baseExperienceRow = base.qualifications.find((row) => row.name === YEARS_EXPERIENCE);
    const baseExperienceKey = baseExperienceRow ? parseInt(baseExperienceRow.value ?? '0', 10) : -1;
    const tierExperienceRow = tier.qualifications.find((row) => row.name === YEARS_EXPERIENCE);
    const tierExperienceKey = tierExperienceRow ? parseInt(tierExperienceRow.value ?? '0', 10) : null;

    const experience = choices.experience
        .filter((choice) => choice.key > baseExperienceKey && choice.key !== tierExperienceKey)
        .map((choice) => ({
            key: `experience-${choice.key}`,
            label: choice.value,
            chosen: choice.key,
            choices: choices.experience,
            qualification: null,
            category: 'experience' as QualificationCategory,
        }));

    const usedBooleanIds = new Set([...base.qualifications, ...tier.qualifications].map((row) => row.qualification_id));
    const booleans = choices.boolean
        .filter((choice) => !usedBooleanIds.has(choice.key))
        .map((choice) => ({
            key: `boolean-${choice.key}`,
            label: choice.value,
            chosen: choice.key,
            choices: [choice],
            qualification: choice.qualification,
            category: 'boolean' as QualificationCategory,
        }));

    const singles = choices.single.flatMap(({ qualification, choices: singleChoices }) => {
        const baseOption = base.qualifications.find((row) => row.qualification_id === qualification.id)?.option_id;
        const tierOption = tier.qualifications.find((row) => row.qualification_id === qualification.id)?.option_id;
        return singleChoices
            .filter((choice) => choice.key !== -1 && choice.key !== baseOption && choice.key !== tierOption)
            .map((choice) => ({
                key: `single-${qualification.id}-${choice.key}`,
                label: `${qualification.name}: ${choice.value}`,
                chosen: choice.key,
                choices: singleChoices,
                qualification,
                category: 'single' as QualificationCategory,
            }));
    });

    return [
        { heading: 'Education', options: education },
        { heading: 'Experience', options: experience },
        { heading: 'Credentials', options: [...booleans, ...singles] },
    ].filter((group) => group.options.length > 0);
}
